import { useState } from 'react';
import { addDays, cnDate, fmtMonthDay, type SettingsDTO } from '@wd/shared';
import { timeCN, todayCN } from '../../lib/clock';
import { useAuditLog } from '../../lib/queries';
import { Button, Modal, Skeleton } from '../../ui/primitives';
import { Choice, Group, Head, Row, SettingChoice } from './parts';

// 设置 › 数据与日志（设计稿 22e）：保留期限、导出、操作日志。仅管理员。

type ExportKind = 'reports' | 'projects' | 'anomalies';
const KINDS: { value: ExportKind; label: string }[] = [
  { value: 'reports', label: '日报' },
  { value: 'projects', label: '项目动态' },
  { value: 'anomalies', label: '异常处理记录' },
];

export function DataSection({ dto }: { dto: SettingsDTO }) {
  const [exporting, setExporting] = useState(false);
  const log = useAuditLog(dto.canEdit);
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];
  return (
    <>
      <Head title="数据与日志" desc="日报原文保存多久、怎么导出，以及谁在什么时候做了关键操作。" />
      <Group title="保留与导出">
        <Row label="日报保留期限" desc="到期后自动删除日报原文，项目动态中只保留“某天有进展”的记录">
          <SettingChoice
            dto={dto}
            k="reportRetentionYears"
            options={[
              { value: 1, label: '1 年' },
              { value: 2, label: '2 年' },
              { value: 3, label: '3 年' },
              { value: 5, label: '5 年' },
              { value: 0, label: '永久' },
            ]}
            width={120}
          />
        </Row>
        <Row label="“建议关注”记录" desc="包括 AI 观察和主管备注">
          <SettingChoice
            dto={dto}
            k="insightRetentionWeeks"
            options={[4, 8, 12, 26].map((w) => ({ value: w, label: `${w} 周` }))}
            width={120}
          />
        </Row>
        <Row label="导出数据" desc="日报、项目动态、异常处理记录，CSV 格式；导出会写入操作日志">
          <Button onClick={() => setExporting(true)}>导出…</Button>
        </Row>
      </Group>

      <Group title="操作日志" right={<span>仅管理员可见</span>}>
        {log.isLoading && <Skeleton w={260} style={{ margin: '12px 0' }} />}
        {!log.isLoading && entries.length === 0 && <div className="st-empty">还没有记录。</div>}
        <div className="st-log">
          {entries.map((e) => {
            const d = cnDate(new Date(e.at));
            return (
              <div key={e.id} className="st-log-row">
                <span className="when">
                  {d === todayCN() ? '今天' : fmtMonthDay(d)} {timeCN(e.at)}
                </span>
                <span className="who">{e.actorName ?? '系统'}</span>
                <span className="what">{e.text}</span>
              </div>
            );
          })}
        </div>
        {log.hasNextPage && (
          <button type="button" className="st-more" disabled={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
            {log.isFetchingNextPage ? '加载中…' : '更早的记录'}
          </button>
        )}
      </Group>
      {exporting && <ExportModal onClose={() => setExporting(false)} />}
    </>
  );
}

function ExportModal({ onClose }: { onClose: () => void }) {
  const today = todayCN();
  const [kind, setKind] = useState<ExportKind>('reports');
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const presets: { label: string; from: string; to: string }[] = [
    { label: '本月', from: `${today.slice(0, 7)}-01`, to: today },
    { label: '上个月', from: `${addDays(`${today.slice(0, 7)}-01`, -1).slice(0, 7)}-01`, to: addDays(`${today.slice(0, 7)}-01`, -1) },
    { label: '最近 90 天', from: addDays(today, -89), to: today },
  ];
  const href = `/api/admin/export?kind=${kind}&from=${from}&to=${to}`;
  return (
    <Modal
      width={460}
      title="导出数据"
      subtitle="CSV，用 Excel 或 WPS 打开"
      onClose={onClose}
      footNote="导出会写入操作日志"
      footer={
        <a className="wd-btn primary" href={href} download onClick={() => setTimeout(onClose, 300)} aria-disabled={from > to}>
          下载
        </a>
      }
    >
      <div className="st-form">
        <label>
          <span className="k">内容</span>
          <Choice value={kind} options={KINDS} onChange={setKind} width={180} />
        </label>
        <div>
          <span className="k t-group">时间范围</span>
          <div className="row" style={{ gap: 8, marginTop: 6 }}>
            <input className="wd-input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="开始日期" />
            <span className="t-meta">至</span>
            <input className="wd-input" type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} aria-label="结束日期" />
          </div>
          <div className="row" style={{ gap: 6, marginTop: 8 }}>
            {presets.map((p) => (
              <Button
                key={p.label}
                variant="text"
                onClick={() => {
                  setFrom(p.from);
                  setTo(p.to);
                }}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
